import { useCallback, useEffect, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { CatalogVersion } from '@core/db/models'
import {
  btn,
  btnBase,
  btnSize,
  cx,
  fieldInput,
  fieldLabel,
  hint,
  modalLg,
  overlay
} from '@ui/classes'
import { badgeActive, errBanner, okBanner, versionRow } from '@ui/admin'
import { httpErrorText } from '@core/utils/httpError'
import { money } from '@core/utils/format'
import { syncService } from '@core/services/sync/SyncService'
import { useAuthToken } from './useAuthToken'
import { CLOUD_API } from './shared'

/** Запись журнала изменений каталога (ARCH-TASK-60). */
interface CatalogChange {
  id: number
  at: string
  source: 'import' | 'pos' | 'admin' | 'publish' | 'rollback'
  kind: 'add' | 'update' | 'delete' | 'publish'
  name: string | null
  /** Штрихкод (null — у старых записей неизвестен). */
  barcode: string | null
  field: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  author_login: string | null
}

const KIND_LABEL: Record<CatalogChange['kind'], string> = {
  add: 'Добавлен',
  update: 'Изменён',
  delete: 'Удалён',
  publish: 'Публикация'
}

const FIELD_LABEL: Record<string, string> = {
  price: 'цена',
  min_stock: 'мин. остаток',
  name: 'название',
  stock: 'остаток',
  category: 'категория',
  unit: 'ед. изм.',
  is_weight: 'весовой',
  requires_marking: 'маркировка'
}

function fmtValue(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  return String(value)
}

/** Человекочитаемая дельта записи: «цена 100 → 120». */
function deltaText(change: CatalogChange): string | null {
  // Маркер усечения пачки — это не изменение полей.
  if (change.field === '__truncated__') return null
  if (change.kind !== 'update') return null
  const before = change.before ?? {}
  const after = change.after ?? {}
  const keys = Object.keys(after)
  if (keys.length === 0) return null
  return keys
    .map(
      (key) =>
        `${FIELD_LABEL[key] ?? key}: ${fmtField(key, before[key])} → ${fmtField(key, after[key])}`
    )
    .join(', ')
}

/** Значение поля для ленты: цена — в валюте, остальное как есть. */
function fmtField(key: string, value: unknown): string {
  if (key === 'price') {
    const num = Number(value)
    if (value != null && Number.isFinite(num)) return money(num)
  }
  return fmtValue(value)
}

/** Цена товара для строки (у удаления — из before). */
function priceText(change: CatalogChange): string | null {
  const raw = change.kind === 'delete' ? change.before?.price : change.after?.price
  const value = Number(raw)
  return raw != null && Number.isFinite(value) ? money(value) : null
}

/**
 * Детали товара для второй строки: ШК, категория, единица, «весовой»,
 * «маркировка». Для старых записей ШК неизвестен — сегмент не показываем.
 */
function productDetails(change: CatalogChange): string[] {
  const data = (change.kind === 'delete' ? change.before : change.after) ?? {}
  const parts: string[] = []
  const barcode =
    change.barcode ?? (typeof data.barcode === 'string' ? data.barcode : null)
  if (barcode === '') parts.push('без ШК')
  else if (barcode != null) parts.push(`ШК ${barcode}`)
  if (typeof data.category === 'string' && data.category.trim()) parts.push(data.category)
  if (typeof data.unit === 'string' && data.unit.trim()) parts.push(data.unit)
  if (data.is_weight === true) parts.push('весовой')
  if (data.requires_marking === true) parts.push('маркировка')
  return parts
}

/** Группа изменений — одна операция (импорт, одобрение, публикация). */
const SOURCE_TITLE: Record<CatalogChange['source'], string> = {
  import: 'Импорт прайса',
  pos: 'Заявка с кассы',
  admin: 'Правка цены',
  publish: 'Публикация версии',
  rollback: 'Откат версии'
}

/** Запрос к журналу изменений (общий для групп и деталей). */
async function fetchChanges<T>(
  token: string,
  query: string
): Promise<{ items: T[]; total: number; next_before_id: number | null }> {
  const response = await fetch(`${CLOUD_API}/catalog/changes?${query}`, {
    headers: { Authorization: `Bearer ${token}` }
  })
  if (!response.ok) {
    throw new Error(httpErrorText('История изменений', response.status))
  }
  return (await response.json()) as {
    items: T[]
    total: number
    next_before_id: number | null
  }
}

/** Строка детализации внутри раскрытой операции. */
function ChangeRow({ change }: { change: CatalogChange }) {
  if (change.field === '__truncated__') {
    return (
      <li className="py-0.5 text-sm">
        <span className="font-semibold">Пропущено</span>
        {change.name ? ` · ${change.name}` : ''}
      </li>
    )
  }

  const delta = deltaText(change)
  const price =
    change.kind === 'add' || change.kind === 'delete' ? priceText(change) : null
  const details = productDetails(change)

  return (
    <li className="py-1 text-sm">
      <div>
        <span className="font-semibold">{KIND_LABEL[change.kind] ?? change.kind}</span>
        {change.name ? ` · ${change.name}` : ''}
        {price && ` · ${price}`}
        {delta && <span className={hint}> — {delta}</span>}
      </div>
      {details.length > 0 && <div className={hint}>{details.join(' · ')}</div>}
    </li>
  )
}

/**
 * «Что изменилось» под версией каталога: операции из журнала изменений.
 * Каждая операция — одна строка («Импорт прайса — +12 новых · 45 цен»),
 * детали раскрываются по клику. Грузится лениво.
 */
/** Сводка по группе изменений: «+2 новых · 1 цен · 3 удалено». */
function summarizeChanges(list: CatalogChange[]): string {
  let added = 0
  let updated = 0
  let removed = 0
  let price = 0
  for (const change of list) {
    if (change.kind === 'add') added += 1
    else if (change.kind === 'delete') removed += 1
    else if (change.kind === 'update') {
      updated += 1
      if (change.field === 'price') price += 1
    }
  }
  const parts: string[] = []
  if (added) parts.push(`+${added} новых`)
  if (price) parts.push(`${price} цен`)
  const other = updated - price
  if (other > 0) parts.push(`${other} правок`)
  if (removed) parts.push(`${removed} удалено`)
  return parts.join(' · ')
}

/**
 * «Что изменилось» под версией/подверсией.
 *
 * Внутри одного чекпоинта изменения группируются по источнику: две заявки,
 * сделанные в одном 10-минутном окне, показываются одним блоком «Заявка с
 * кассы», а не двумя. Даты/автор не дублируются — они в заголовке чекпоинта.
 */
function VersionChanges({ versionId }: { versionId: number }) {
  const token = useAuthToken()
  const [items, setItems] = useState<CatalogChange[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setItems([])
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const data = await fetchChanges<CatalogChange>(token, `version=${versionId}&limit=200`)
        if (!cancelled) setItems(data.items)
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, versionId])

  if (error) return <p className={errBanner}>{error}</p>
  if (items == null) return <p className={hint}>Загрузка изменений…</p>
  if (items.length === 0) return <p className={hint}>Изменений по этой версии нет.</p>

  const groups = new Map<CatalogChange['source'], CatalogChange[]>()
  for (const item of items) {
    const list = groups.get(item.source)
    if (list) list.push(item)
    else groups.set(item.source, [item])
  }
  const showHeaders = groups.size > 1

  return (
    <>
      {[...groups.entries()].map(([source, list]) => (
        <div key={source} className="py-1">
          {showHeaders && (
            <div className="text-sm font-semibold">
              {SOURCE_TITLE[source] ?? source}
              <span className={hint}> — {summarizeChanges(list)}</span>
            </div>
          )}
          <ul className="m-0 list-none">
            {list.map((change) => (
              <ChangeRow key={change.id} change={change} />
            ))}
          </ul>
        </div>
      ))}
      {items.length >= 200 && (
        <p className={hint}>Показаны последние 200 изменений версии.</p>
      )}
    </>
  )
}

/** Версия/чекпоинт из облака (ARCH-TASK-61a). */
interface CloudVersion {
  id: number
  version_name: string
  is_active: boolean
  kind: 'major' | 'minor'
  parent_id: number | null
  note: string | null
  counts: {
    added?: number
    updated?: number
    removed?: number
    price_changed?: number
  } | null
  created_at: string
  author_login: string | null
  /** Первые имена изменений (превью свёрнутой подверсии). */
  preview: string[]
  children: CloudVersion[]
  /** Снапшот версии (ARCH-TASK-61b): есть и не удалён. */
  has_snapshot: boolean
  /** Сколько товаров в снапшоте (null — снапшота нет). */
  product_count: number | null
  /** Когда снапшот удалён вручную (null — не удалялся). */
  snapshot_deleted_at: string | null
}

/** Строка счётчиков чекпоинта: «+12 новых · 45 цен · 3 удалено». */
function formatCounts(counts: CloudVersion['counts']): string {
  if (!counts) return ''
  const parts: string[] = []
  if (counts.added) parts.push(`+${counts.added} новых`)
  if (counts.price_changed) parts.push(`${counts.price_changed} цен`)
  const otherUpdates = (counts.updated ?? 0) - (counts.price_changed ?? 0)
  if (otherUpdates > 0) parts.push(`${otherUpdates} правок`)
  if (counts.removed) parts.push(`${counts.removed} удалено`)
  return parts.join(' · ')
}

/** Кнопка «Что изменилось» с локальным раскрытием для одной версии. */
function VersionChangesButton({ versionId }: { versionId: number }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cx(btnBase, btn.secondary, btnSize.sm)}
      >
        {open ? 'Скрыть изменения' : 'Что изменилось'}
      </button>
      {open && (
        <div className="mt-1">
          <VersionChanges versionId={versionId} />
        </div>
      )}
    </>
  )
}

/** Ответ превью отката (ARCH-TASK-61b). */
interface RollbackDiff {
  target: { id: number; version_name: string; product_count: number | null }
  counts: { added?: number; updated?: number; removed?: number; price_changed?: number }
  unchanged: number
  sample: {
    restore: { name: string; barcode: string; unarchive: boolean }[]
    remove: { name: string; barcode: string; price: number }[]
    update: { name: string; barcode: string; changes: Record<string, [unknown, unknown]> }[]
  }
}

/** Человеческий текст ошибки отката по коду из ответа облака. */
const ROLLBACK_ERROR: Record<string, string> = {
  rollback_available_for_major_only: 'Откат возможен только к крупной версии',
  version_is_active: 'Это текущая версия каталога — откатывать некуда',
  snapshot_missing: 'У версии нет снапшота — откат недоступен',
  snapshot_deleted: 'Снапшот версии удалён — откат недоступен',
  snapshot_corrupted: 'Снапшот повреждён — откат заблокирован, чтобы не потерять каталог',
  nothing_to_rollback: 'Каталог уже совпадает с этой версией — откатывать нечего',
  version_not_found: 'Версия не найдена в облаке'
}

/** Короткий список имён в модале отката: «Вернутся (первые 3 из 40)» + строки. */
function DiffList({ title, items, total }: { title: string; items: string[]; total?: number }) {
  if (items.length === 0) return null
  const truncated = total != null && total > items.length
  return (
    <div className="mb-2">
      <div className="text-sm font-semibold">
        {title}
        {truncated && (
          <span className="font-normal text-muted">
            {' '}
            — первые {items.length} из {total}
          </span>
        )}
      </div>
      <ul className="m-0 list-none">
        {items.map((text) => (
          <li key={text} className="py-0.5 text-sm text-muted">
            {text}
          </li>
        ))}
      </ul>
    </div>
  )
}


/** Сводка превью: «+2 вернётся · −3 удалится · 1 цен · 4 без изменений». */
function diffSummary(diff: RollbackDiff): string {
  const parts: string[] = []
  if (diff.counts.added) parts.push(`+${diff.counts.added} вернётся`)
  if (diff.counts.removed) parts.push(`−${diff.counts.removed} удалится`)
  if (diff.counts.price_changed) parts.push(`${diff.counts.price_changed} цен`)
  const other = (diff.counts.updated ?? 0) - (diff.counts.price_changed ?? 0)
  if (other > 0) parts.push(`${other} правок`)
  parts.push(`${diff.unchanged} без изменений`)
  return parts.join(' · ')
}

/** Поля изменения: «цена: 52,00 ₽ → 45,00 ₽ · весовой: нет → да». */
function describeChanges(changes: Record<string, [unknown, unknown]>): string {
  return Object.entries(changes)
    .map(
      ([key, [before, after]]) =>
        `${FIELD_LABEL[key] ?? key}: ${fmtField(key, before)} → ${fmtField(key, after)}`
    )
    .join(', ')
}

/**
 * Модальное окно отката (ARCH-TASK-61b): превью изменений, комментарий,
 * подтверждение. Откат создаёт новую версию — старая остаётся в истории.
 */
function RollbackModal({
  token,
  version,
  onClose,
  onDone
}: {
  token: string
  version: CloudVersion
  onClose: () => void
  /** `refreshCatalog` — сразу подтянуть дельту в локальный каталог. */
  onDone: (message: string, refreshCatalog?: boolean) => void
}) {
  const [diff, setDiff] = useState<RollbackDiff | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  /** Второй клик по «Удалить снапшот» подтверждает действие. */
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Превью считает сервер тем же кодом, что и сам откат — цифры совпадут.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch(`${CLOUD_API}/catalog/versions/${version.id}/diff`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response.ok) {
          throw new Error(httpErrorText('Превью отката', response.status))
        }
        const data = (await response.json()) as RollbackDiff
        if (!cancelled) setDiff(data)
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, version.id])

  /** Запрос к откату/удалению снапшота с разбором кода ошибки. */
  const send = async (path: string, init: RequestInit, action: string): Promise<Response> => {
    const response = await fetch(`${CLOUD_API}/catalog/versions/${version.id}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
    if (!response.ok) {
      let code = ''
      try {
        code = ((await response.json()) as { error?: string }).error ?? ''
      } catch {
        // Не JSON — покажем общий текст по статусу.
      }
      throw new Error(ROLLBACK_ERROR[code] ?? httpErrorText(action, response.status))
    }
    return response
  }

  const apply = async () => {
    setBusy(true)
    setError(null)
    try {
      const response = await send(
        '/rollback',
        { method: 'POST', body: JSON.stringify({ note }) },
        'Откат каталога'
      )
      const data = (await response.json()) as {
        version: { id: number; version_name: string }
        applied: { added: number; restored: number; updated: number; removed: number }
      }
      const changed =
        data.applied.added + data.applied.restored + data.applied.updated + data.applied.removed
      onDone(
        `Создана версия №${data.version.id} «${data.version.version_name}» — обновлено ${changed} поз.`,
        true
      )
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const removeSnapshot = async () => {
    setBusy(true)
    setError(null)
    try {
      await send('/snapshot', { method: 'DELETE' }, 'Удаление снапшота')
      onDone('Снапшот версии удалён — откат к ней больше недоступен')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const hasChanges =
    diff != null &&
    (diff.counts.added ?? 0) + (diff.counts.removed ?? 0) + (diff.counts.updated ?? 0) > 0

  return (
    <div className={overlay}>
      <div className={modalLg}>
        <h3 className="mt-0 mb-2 text-lg font-bold">
          Откатить каталог к «{version.version_name}»?
        </h3>
        <p className={hint}>
          Будет создана новая версия с состоянием каталога на момент публикации этой версии.
          Правки, сделанные после (подверсии), тоже вернутся назад — они перечислены ниже.
          Кассы подхватят изменения при синхронизации, вручную ничего делать не нужно.
        </p>
        {error && <p className={errBanner}>{error}</p>}
        {diff == null ? (
          <p className={hint}>Считаем изменения…</p>
        ) : (
          <>
            <p className="my-2 text-sm font-semibold">{diffSummary(diff)}</p>
            {diff.counts.added === 0 &&
              diff.counts.removed === 0 &&
              diff.counts.updated === 0 && (
                <p className={hint}>Каталог уже совпадает с этой версией.</p>
              )}
            <DiffList
              title="Вернутся"
              items={diff.sample.restore.map((x) => x.name)}
              total={diff.counts.added}
            />
            <DiffList
              title="Удалятся"
              items={diff.sample.remove.map((x) => `${x.name} · ${money(x.price)}`)}
              total={diff.counts.removed}
            />
            <DiffList
              title="Изменятся"
              items={diff.sample.update.map((x) => `${x.name}: ${describeChanges(x.changes)}`)}
              total={diff.counts.updated}
            />
          </>
        )}
        <label className={fieldLabel}>
          Комментарий (необязательно)
          <input
            className={fieldInput}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder={`Откат к версии №${version.id}`}
          />
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => void apply()}
            disabled={busy || diff == null || !hasChanges}
            className={cx(btnBase, btn.primary, btnSize.md)}
          >
            {busy ? 'Откатываем…' : 'Откатить'}
          </button>
          <button
            onClick={onClose}
            disabled={busy}
            className={cx(btnBase, btn.secondary, btnSize.md)}
          >
            Отмена
          </button>
          <button
            onClick={() => (confirmDelete ? void removeSnapshot() : setConfirmDelete(true))}
            disabled={busy}
            className={cx(btnBase, btn.ghost, btnSize.sm)}
          >
            {confirmDelete ? 'Точно удалить?' : 'Удалить снапшот'}
          </button>
        </div>
        <p className={hint}>
          Удаление снапшота освобождает место, но откат к этой версии станет недоступен.
        </p>
      </div>
    </div>
  )
}

/** Дерево облачных версий: major с вложенными minor-подверсиями. */
function CloudVersionTree({
  items,
  token,
  onRollbackDone
}: {
  items: CloudVersion[]
  token: string | null
  /** Сообщение после успешного отката/удаления снапшота + флаг обновления каталога. */
  onRollbackDone: (message: string, refreshCatalog?: boolean) => void
}) {
  const [target, setTarget] = useState<CloudVersion | null>(null)
  return (
    <>
      <ul className="m-0 list-none p-0">
        {items.map((v) => (
          <li key={v.id} className="border-b border-line py-2 last:border-0">
            <div className="flex flex-wrap items-center gap-2">
              <strong>{v.version_name}</strong>
              {v.is_active && <span className={badgeActive}>активна</span>}
              <span className={hint}>{v.kind === 'major' ? 'крупная' : 'подверсия'}</span>
            </div>
            <div className={hint}>
              {new Date(v.created_at).toLocaleString('ru-RU')} · №{v.id}
              {v.note ? ` · ${v.note}` : ''}
              {v.author_login ? ` · ${v.author_login}` : ''}
            </div>
            {formatCounts(v.counts) && <div className={hint}>{formatCounts(v.counts)}</div>}
            {v.kind === 'major' && (
              <div className={hint}>
                {v.has_snapshot
                  ? `Снапшот на момент публикации: ${v.product_count ?? 0} поз.`
                  : v.snapshot_deleted_at
                    ? 'Снапшот удалён — откат недоступен'
                    : 'Снапшота нет — откат недоступен'}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {/* У версии с подверсиями все изменения живут в детях — вторая
                  кнопка «Что изменилось» только путает. */}
              {v.children.length === 0 && <VersionChangesButton versionId={v.id} />}
              {v.kind === 'major' && (
                <button
                  onClick={() => setTarget(v)}
                  disabled={v.is_active || !v.has_snapshot}
                  title={
                    v.is_active
                      ? 'Это текущая версия каталога'
                      : v.has_snapshot
                        ? 'Вернуть каталог к состоянию на момент публикации версии'
                        : v.snapshot_deleted_at
                          ? 'Снапшот удалён — откат недоступен'
                          : 'Версия создана до появления снапшотов — откат недоступен'
                  }
                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                >
                  {v.is_active ? 'Текущая' : 'Откатить сюда'}
                </button>
              )}
            </div>

          {v.children.length > 0 && (
            <ul className="m-0 mt-2 list-none border-l border-line pl-3">
              {v.children.map((child) => (
                <li key={child.id} className="border-t border-line py-1.5 text-sm first:border-0">
                  <div className={hint}>
                    {new Date(child.created_at).toLocaleString('ru-RU')} · подверсия №{child.id}
                    {child.author_login ? ` · ${child.author_login}` : ''}
                  </div>
                  {formatCounts(child.counts) && (
                    <div className={hint}>
                      {formatCounts(child.counts)}
                      {child.preview.length > 0 && ` — ${child.preview.join(', ')}`}
                    </div>
                  )}
                  <VersionChangesButton versionId={child.id} />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
      </ul>
      {target && token && (
        <RollbackModal
          token={token}
          version={target}
          onClose={() => setTarget(null)}
          onDone={(message, refreshCatalog) => {
            setTarget(null)
            onRollbackDone(message, refreshCatalog)
          }}
        />
      )}
    </>
  )
}

/**
 * История версий каталога с откатом и журналом изменений.
 * Перечитывается при изменении refreshKey (после локального импорта или отката).
 */
export function CatalogVersionsSection({
  core,
  refreshKey,
  onRolledBack
}: {
  core: AppCore
  refreshKey: number
  onRolledBack?: () => void
}) {
  const { catalog } = core
  const token = useAuthToken()
  const [cloudVersions, setCloudVersions] = useState<CloudVersion[] | null>(null)
  const [versions, setVersions] = useState<CatalogVersion[]>([])
  const [activeVersionId, setActiveVersionId] = useState<number | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Перечитать историю из облака (после отката или удаления снапшота). */
  const [cloudReload, setCloudReload] = useState(0)

  const load = useCallback(async () => {
    await catalog.ensureVersionSources()
    const [vs, activeId] = await Promise.all([
      catalog.listVersions(),
      catalog.getActiveVersionId()
    ])
    setVersions(vs)
    setActiveVersionId(activeId)
  }, [catalog])

  useEffect(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
  }, [load, refreshKey])

  // История чекпоинтов из облака (major/minor, счётчики, автор).
  useEffect(() => {
    if (!token) {
      setCloudVersions(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch(`${CLOUD_API}/catalog/versions?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response.ok) {
          throw new Error(httpErrorText('История версий', response.status))
        }
        const data = (await response.json()) as { items?: CloudVersion[] }
        if (!cancelled) setCloudVersions(data.items ?? [])
      } catch {
        // Офлайн или старая сборка облака — остаёмся на локальном списке.
        if (!cancelled) setCloudVersions(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, refreshKey, cloudReload])

  /**
   * Откат выполнен в облаке: сообщение + перечитать историю и каталог.
   * `refreshCatalog` — сразу тянем дельту, иначе касса покажет старые цены
   * до следующего 60-секундного цикла синхронизации.
   */
  const handleRollbackDone = (text: string, refreshCatalog = false) => {
    setMessage(text)
    setError(null)
    setCloudReload((n) => n + 1)
    if (refreshCatalog && token) {
      void syncService
        .pullCatalog(token)
        .catch(() => undefined)
        .then(() => onRolledBack?.())
      return
    }
    onRolledBack?.()
  }

  return (
    <div>
      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}
      {cloudVersions && cloudVersions.length > 0 ? (
        <CloudVersionTree
          items={cloudVersions}
          token={token}
          onRollbackDone={handleRollbackDone}
        />
      ) : versions.length === 0 ? (
        <p className={hint}>Версий пока нет.</p>
      ) : (
        <>
          <ul className="m-0 list-none p-0">
            {versions.map((v) => (
              <li key={v.id} className={versionRow}>
                <div>
                  <strong>{v.versionName}</strong>
                  {v.id === activeVersionId && <span className={badgeActive}>активна</span>}
                  <div className={hint}>
                    {new Date(v.createdAt).toLocaleString('ru-RU')}
                    {v.note ? ` · ${v.note}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className={hint}>
            Это локальный список версий кассы. Откат каталога выполняется в облачной истории —
            подключите облако, чтобы вернуть прежнее состояние.
          </p>
        </>
      )}
    </div>
  )
}

